/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import axiosInstance from '../functions/AxiosInstance'; // Asegúrate de que la ruta sea correcta

// Función para crear una nueva licencia
const createLicense = async (licenseData: {
  name: string;
  description: string;
  price: number;
  commercialUse: boolean;
  validity: 'perpetual' | 'time-limited';
  duration: number;
  amountOfSites: number;
}) => {
  const response = await axiosInstance.post('/licenses', licenseData);
  return response.data; // Devuelve los datos de la respuesta
};

// Hook para crear una licencia
export const useSubmitLicense = () => {
  const mutation = useMutation({
    mutationFn: createLicense, // Función para crear la licencia
    onSuccess: () => {
      toast.success('Licencia creada con éxito.');
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Error desconocido';
      toast.error(`Error al crear la licencia: ${errorMessage}`);
      console.error('Error al crear la licencia:', errorMessage);
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isPending, // Estado de carga de la mutación
    isSuccess: mutation.isSuccess, // Indica si la creación fue exitosa
  };
};



// Función para obtener las licencias
const fetchLicenses = async () => {
  const response = await axiosInstance.get('/licenses');
  return response.data.licenses; // Devuelve solo el array de licencias de la respuesta
};

// Hook para listar las licencias
export const useGetLicenses = () => {
  const { data, error, isLoading, isError } = useQuery({
    queryKey: ['licenses'], // Esto es lo que identifica la consulta en React Query
    queryFn: async () => {
      try {
        return await fetchLicenses();
      } catch (error: any) {
        console.error('Error al obtener las licencias:', error);
        throw error;
      }
    },
    refetchOnWindowFocus: false, // Opcional: Evita que los datos se vuelvan a obtener cuando el foco de la ventana cambia
  });

  return {
    licenses: data || [],    // Las licencias obtenidas (garantiza que siempre devuelvas un array vacío si no hay datos)
    isLoading,               // Si está cargando
    isError,                 // Si hubo un error
    error,                   // El error completo (si existe)
  };
};


// Función para obtener una licencia por su ID
const fetchLicenseById = async (licenseId: string) => {
  const response = await axiosInstance.get(`/licenses/${licenseId}`);
  return response.data; // Devuelve los datos de la respuesta
};

// Hook para obtener una licencia por ID
export const useGetLicenseById = (licenseId: string) => {
  const { data, error, isLoading, isError } = useQuery({
    queryKey: ['license', licenseId], // Identificador único para la consulta (utilizando el licenseId)
    queryFn: async () => {
      try {
        return await fetchLicenseById(licenseId); // Llama a la función para obtener la licencia
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        console.error('Error al obtener la licencia:', error);
        throw error;
      }
    },
    refetchOnWindowFocus: false, // Opcional: Evita que los datos se vuelvan a obtener cuando el foco de la ventana cambia
  });

  return {
    license: data,     // La licencia obtenida
    isLoading,         // Estado de carga
    isError,           // Si hubo un error
    error,             // El error completo (si existe)
  };
};



// Función para actualizar una licencia por su ID
const updateLicense = async ({ licenseId, licenseData }: { licenseId: string, licenseData: any }) => {
  const response = await axiosInstance.put(`/licenses/${licenseId}`, licenseData);
  return response.data; // Devuelve los datos de la respuesta
};

// Hook para actualizar una licencia
export const useUpdateLicense = () => {
  const mutation = useMutation({
    mutationFn: updateLicense, // Función para actualizar la licencia
    onSuccess: () => {
      toast.success('Licencia actualizada con éxito.');
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Error desconocido';
      toast.error(`Error al actualizar la licencia: ${errorMessage}`);
      console.error('Error al actualizar la licencia:', errorMessage);
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isPending, // Estado de carga de la mutación
    isSuccess: mutation.isSuccess, // Indica si la actualización fue exitosa
  };
};


// Función para eliminar una licencia por su ID
const deleteLicense = async (licenseId: string) => {
  const response = await axiosInstance.delete(`/licenses/${licenseId}`);
  return response.data; // Devuelve los datos de la respuesta
};

// Hook para eliminar una licencia
export const useDeleteLicense = () => {
  const mutation = useMutation({
    mutationFn: deleteLicense, // Función para eliminar la licencia
    onSuccess: () => {
      toast.success('Licencia eliminada con éxito.');
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Error desconocido';
      toast.error(`Error al eliminar la licencia: ${errorMessage}`);
      console.error('Error al eliminar la licencia:', errorMessage);
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isPending, // Estado de carga de la mutación
    isSuccess: mutation.isSuccess, // Indica si la eliminación fue exitosa
  };
};
